import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { api } from "@/lib/api";
import { GOOGLE_REVIEW } from "@/data/business";

function GoogleGlyph({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.3 9.14 5.38 12 5.38Z" />
    </svg>
  );
}

function Stars({ n = 5 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className={`h-4 w-4 ${i < Math.round(n) ? "fill-yellow-400 text-yellow-400" : "text-slate-300"}`} />
      ))}
    </div>
  );
}

// Normalize both sources (GBP v4 + Places API) into one shape.
function normalize(gbp, places) {
  if (gbp?.success && gbp.reviews?.length) {
    return {
      live: true,
      rating: gbp.rating,
      count: gbp.total_reviews,
      reviews: gbp.reviews.map((r) => ({
        rating: r.rating, text: r.text, when: r.relative_time,
        name: r.author, photo: r.photo, reply: r.reply,
      })),
    };
  }
  if (places?.configured && !places.error && places.reviews?.length) {
    return {
      live: false,
      rating: places.rating,
      count: places.reviewCount,
      reviews: places.reviews.map((r) => ({
        rating: r.rating, text: r.text, when: r.relativeTime,
        name: r.authorName, photo: r.authorPhotoUri, reply: "",
      })),
    };
  }
  return null;
}

export default function GoogleReviewsLive() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      let gbp = null, places = null;
      try { gbp = (await api.get("/reviews/google")).data; } catch (e) {}
      if (!(gbp?.success && gbp.reviews?.length)) {
        try { places = (await api.get("/google-reviews")).data; } catch (e) {}
      }
      if (alive) setData(normalize(gbp, places));
    })();
    return () => { alive = false; };
  }, []);

  if (!data) return null;
  const shown = data.reviews.slice(0, 6);

  return (
    <div className="mt-14" data-testid="google-reviews-live">
      <div className="flex flex-col items-center text-center">
        <div className="inline-flex items-center gap-3 bg-white border border-slate-200 rounded-full pl-3 pr-5 py-2 shadow-sm">
          <GoogleGlyph className="h-6 w-6" />
          <span className="font-heading text-2xl font-black text-slate-900">{Number(data.rating).toFixed(1)}</span>
          <Stars n={data.rating} />
          <span className="text-sm text-slate-500">({data.count} Google reviews)</span>
        </div>
        <p className="mt-3 inline-flex items-center gap-2 text-sm text-slate-500">
          {data.live && (
            <span className="inline-flex items-center gap-1.5 text-[#16a34a] font-semibold" data-testid="google-live-badge">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              Live
            </span>
          )}
          from our verified Google Business Profile
        </p>
      </div>

      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {shown.map((r, i) => (
          <div
            key={i}
            className="bg-white border border-slate-200 rounded-sm p-6 flex flex-col"
            data-testid={`google-review-${i}`}
          >
            <div className="flex items-center justify-between">
              <Stars n={r.rating} />
              <GoogleGlyph className="h-5 w-5" />
            </div>
            {r.text && <blockquote className="mt-3 text-slate-700 leading-relaxed line-clamp-5">"{r.text}"</blockquote>}
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-3">
              {r.photo ? (
                <img src={r.photo} alt={`${r.name || "Google reviewer"} profile photo`} width="36" height="36" loading="lazy" referrerPolicy="no-referrer" className="h-9 w-9 rounded-full" />
              ) : (
                <span className="grid place-items-center h-9 w-9 rounded-full bg-slate-200 font-bold text-slate-600">
                  {(r.name || "?")[0]}
                </span>
              )}
              <div>
                <p className="font-heading font-bold text-slate-900 text-sm">{r.name}</p>
                <p className="text-xs text-slate-500">{r.when}</p>
              </div>
            </div>
            {r.reply && (
              <div className="mt-4 bg-slate-50 border-l-2 border-[#4285F4] rounded-r-sm p-3" data-testid={`google-review-reply-${i}`}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Response from the owner</p>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed line-clamp-4">{r.reply}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 text-center">
        <a
          href={GOOGLE_REVIEW.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#4285F4] hover:underline"
          data-testid="google-see-all"
        >
          See all our Google reviews →
        </a>
      </div>
    </div>
  );
}
